import React, { useMemo, lazy } from 'react'
import { Redirect, Route } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import AuthRouteWithFallback from '../components/AuthRouteWithFallback'
import HelperModeMessage from '../components/HelperModeMessage'
import ChildSafetyStandards from '../pages/ChildSafetyStandards'
import PrimaryRoutes from '../PrimaryRoutes'
import { generateRouteVariants } from '../helpers/url_helper'

// Lazy loaded pages
const HomeLoggedOutPage = lazy(() => import('../pages/Landing/LandingPage'))
const LandingPageGetApp = lazy(() => import('../pages/Landing/LandingPageGetApp'))
const InvitationPage = lazy(() => import('../pages/Invitation'))
const TogetherQrCode = lazy(() => import('../pages/TogetherQrCode'))

export const PATHS = {
  HELPER_MESSAGE: '/helpermessage',
  ROOT: '/',
  WELCOME: '/welcome',
  INVITATION: '/invitation',
  GET_APP: '/getapp',
  QR: '/qr',
  STORIES: '/stories',
  CHILD_SAFETY_STANDARDS: '/child-safety-standards',
}

export default function useAppRoutes({ isHelping }) {
  const { signedIn, userLoading } = useUser()
  const routeElements = useMemo(() => {
    // Create a fresh component that uses the current signedIn/userLoading values
    const RootComponentWithUser = () => {
      // Wait for user data to load before making routing decisions
      if (userLoading) {
        return null
      }

      if (signedIn) {
        return <Redirect to={PATHS.STORIES} />
      }

      return <HomeLoggedOutPage />
    }

    const rootComponent = isHelping ? HelperModeMessage : RootComponentWithUser
    const baseRoutes = [
      { path: PATHS.GET_APP, component: LandingPageGetApp, requirePersonalData: true },
      { path: PATHS.QR, component: TogetherQrCode, requirePersonalData: true },
      {
        path: PATHS.CHILD_SAFETY_STANDARDS,
        component: ChildSafetyStandards,
        requirePersonalData: true,
      },
      {
        path: isHelping ? PATHS.HELPER_MESSAGE : PATHS.ROOT,
        component: rootComponent,
        requirePersonalData: true,
      },
      { path: PATHS.WELCOME, component: HomeLoggedOutPage, requirePersonalData: true },
      { path: PATHS.INVITATION, component: InvitationPage, requirePersonalData: true },
      {
        path: [
          '/*/:activityGroupId/activities/:subActivityGroupId/:answerId',
          '/*/:activityGroupId/activities/:subActivityGroupId',
          '/stories/:activityGroupId/:cisId/:answerId',
          ...generateRouteVariants(['/stories', '/temple'], '/:activityGroupId/:answerId'),
          '/*',
        ],
        component: PrimaryRoutes,
        requirePersonalData: true,
        isAuthRoute: true,
      },
    ]

    const elements = baseRoutes.map(({ path, component, requirePersonalData, isAuthRoute }) => {
      const key = Array.isArray(path) ? path.join('|') : path

      if (isAuthRoute) {
        return (
          <AuthRouteWithFallback
            key={key}
            fallback={HomeLoggedOutPage}
            path={path}
            component={component}
            requirePersonalData={requirePersonalData}
          />
        )
      }

      return <Route key={key} exact path={path} component={component} requirePersonalData={requirePersonalData} />
    })

    if (isHelping) {
      elements.unshift(<Redirect key="helperRootRedirect" exact from={PATHS.ROOT} to={PATHS.HELPER_MESSAGE} />)
    }

    if (!isHelping) {
      elements.unshift(<Redirect key="helperRedirect" exact from={PATHS.HELPER_MESSAGE} to={PATHS.ROOT} />)
    }

    return elements
  }, [isHelping, signedIn, userLoading])

  return { routeElements }
}
